/**
 * ModernResultsPreview - Compact Results Display
 *
 * Used for Free/Anonymous tiers (L1) to provide focused, clean results.
 * Shows score card + inline annotations + summary.
 *
 * Design: Y2K + Glassmorphism aesthetic
 * Accessibility: WCAG AAA compliant (15.8:1 contrast on summary card)
 *
 * Phase: Priority C - Inline Annotations (Dec 2025)
 * - Tier-aware issue highlighting (first 2 free, rest gated)
 * - Desktop hover popovers / mobile touch bottom sheets
 * - Accept/ignore workflow with revised text
 *
 * Phase 4: Theatrical Mode Integration (Feb 2026)
 * - Uses useNarratorDisplay for tier-aware narrator text
 * - Supports future streaming handoff
 * - Voice-styled text based on tier (playful, balanced, professional)
 */

import React from "react";
import { View, Platform, Text, StyleSheet } from "react-native";
import { TierBody } from "@dramadetector/ui";
import type {
	DramaAnalysisResponse,
	DramaCategory,
	ResultModuleKey,
	ResultsPrimaryActionTreatment,
	ShareTreatment,
} from "@dramadetector/shared";
import { getLikelyImpactLabel } from "@dramadetector/shared";
import { captureEvent } from "@/lib/analytics";
import { useNarratorDisplay } from "@/hooks/useNarratorDisplay";
import {
	useResultsFunnelAnalytics,
	type ResultsFunnelSurface,
} from "@/hooks/useResultsFunnelAnalytics";
import type { UserTier } from "@/lib/narrator/loadingQuips";

import { GlassButton } from "../glass";
import { ViralShareButton } from "../ViralShareButton";
import { ModernScoreCard } from "./ModernScoreCard";
import { PrimaryRewriteCard } from "./PrimaryRewriteCard";
import { GatedInlineAnnotations } from "./inline-annotations";
import { selectPrimaryRewriteDisplay } from "./inline-annotations/utils/mergeIssuesWithRewrites";
import { sharedStyles, getCategoryFromScore } from "./sharedStyles";

function toLikelyImpactCategory(
	category: string | null | undefined,
): DramaCategory | null {
	switch (category) {
		case "safe":
		case "caution":
		case "high_drama":
		case "extreme_drama":
			return category;
		default:
			return null;
	}
}

interface ModernResultsPreviewProps {
	/** Analysis response data */
	analysis: DramaAnalysisResponse;
	/** Original message text (for inline annotations) */
	message: string;
	/** User's tier for inline annotations gating */
	tier?: "anonymous" | "free" | "drama_plus" | "dramax" | "family_member";
	/** Whether analysis is in progress (for narrator loading state) */
	isLoading?: boolean;
	/** Streamed narrator text (partial, for future streaming support) */
	streamedNarrator?: string;
	/** Whether streaming is enabled */
	streamingEnabled?: boolean;
	/** Callback when "Analyze Another" is clicked */
	onNewAnalysis: () => void;
	/** Callback when upgrade is pressed on gated content */
	onUpgrade?: () => void;
	/** Translation function */
	t: (key: string, options?: Record<string, unknown>) => string;
	/** Test ID for testing */
	testID?: string;
	/** Hide the built-in viral share button (used on shared-link pages) */
	hideShareButton?: boolean;
	/** Shared-policy top-level module order for rollback-safe hierarchy changes. */
	moduleOrder?: ResultModuleKey[];
	/** Whether likely-impact framing should be shown near the score. */
	showLikelyImpact?: boolean;
	/** Whether the best rewrite card should be promoted near the top. */
	showPrimaryRewriteCard?: boolean;
	/** Shared-policy share emphasis seam for rollback-safe CTA ordering. */
	shareTreatment?: ShareTreatment;
	/** Shared-policy primary action seam for copy/use-over-share rollout. */
	primaryActionTreatment?: ResultsPrimaryActionTreatment;
	/** Whether this render belongs to home, private results, or shared-link. */
	analyticsSurface?: ResultsFunnelSurface;
}

/**
 * ModernResultsPreview - Compact results for Free/Anonymous users
 *
 * Sections:
 * 1. Score Card (gradient with category badge)
 * 2. Summary Card (white glassmorphism)
 * 3. Action Button ("Analyze Another")
 */
export function ModernResultsPreview({
	analysis,
	message,
	tier = "free",
	isLoading = false,
	streamedNarrator,
	streamingEnabled = false,
	onNewAnalysis,
	onUpgrade,
	t,
	testID = "modern-results-preview",
	hideShareButton = false,
	moduleOrder,
	showLikelyImpact = false,
	showPrimaryRewriteCard = false,
	shareTreatment,
	primaryActionTreatment = "legacy",
	analyticsSurface = "results_page",
}: ModernResultsPreviewProps) {
	// Extract data from nested `result` (unified pipeline) or flat structure (legacy)
	const score = analysis.result?.score ?? analysis.score;
	const summary = analysis.result?.summary ?? analysis.summary;
	const categoryFromResult = analysis.result?.category ?? analysis.category;
	const issues = analysis.result?.issues ?? analysis.issues ?? [];
	const rewrites = analysis.result?.rewrites ?? analysis.rewrites ?? [];

	const category = categoryFromResult || getCategoryFromScore(score);
	const resolvedShareTreatment: ShareTreatment = hideShareButton
		? "hidden"
		: shareTreatment ?? "secondary";
	const shouldPromotePrimaryActions =
		primaryActionTreatment === "copy_use_primary";
	const { trackPrimaryRewriteCopied } = useResultsFunnelAnalytics({
		analysis,
		tier,
		surface: analyticsSurface,
		renderVariant: "preview",
		showLikelyImpact,
		showPrimaryRewriteCard,
		primaryActionTreatment,
		shareTreatment: resolvedShareTreatment,
	});
	const resultsStartTime = React.useRef(Date.now());
	const likelyImpactLabel = showLikelyImpact
		? getLikelyImpactLabel(toLikelyImpactCategory(category))
		: null;
	const primaryRewrite = React.useMemo(
		() =>
			showPrimaryRewriteCard
				? selectPrimaryRewriteDisplay(message, issues, rewrites)
				: null,
		[showPrimaryRewriteCard, message, issues, rewrites],
	);
	const topSectionOrder = React.useMemo<ResultModuleKey[]>(
		() =>
			moduleOrder?.filter(
				(moduleKey) =>
					moduleKey === "summary" ||
					moduleKey === "primary_rewrite" ||
					moduleKey === "narrator",
			) ?? ["narrator", "summary"],
		[moduleOrder],
	);

	// Check if we have issues for inline annotations
	const hasIssues = issues.length > 0;

	// Use narrator display hook for tier-aware narrator text with theatrical mode integration
	// Pass t function for i18n translations of loading quips
	const userTier = tier as UserTier;
	const {
		displayText: narratorText,
		displayState,
		voiceStyle,
		showTypingAnimation,
	} = useNarratorDisplay({
		tier: userTier,
		isAnalyzing: isLoading,
		analysisResult: {
			narrator_intro:
				analysis.result?.narrator_intro ?? analysis.narrator_intro,
			summary: summary,
		},
		streamedNarrator,
		streamingEnabled,
		t,
	});

	// Extract narrator_intro for display (when complete, show both fields for testing)
	const narratorIntro =
		analysis.result?.narrator_intro ?? analysis.narrator_intro;

	// Display text during loading/streaming phase
	const loadingText =
		displayState === "loading" || displayState === "streaming"
			? narratorText
			: null;

	// Track CTA clicks with timing
	const handleAnalyzeAnother = React.useCallback(() => {
		captureEvent("Results_CTA_Click", {
			cta_type: "analyze_another",
			tier: tier as "anonymous" | "free" | "drama_plus" | "dramax",
			platform: Platform.OS as "web" | "ios" | "android",
			time_on_results_ms: Date.now() - resultsStartTime.current,
			score,
			category,
		});
		onNewAnalysis();
	}, [tier, score, category, onNewAnalysis]);

	// Track upgrade CTA clicks
	const handleUpgrade = React.useCallback(() => {
		captureEvent("Results_CTA_Click", {
			cta_type: "upgrade",
			tier: tier as "anonymous" | "free" | "drama_plus" | "dramax",
			platform: Platform.OS as "web" | "ios" | "android",
			time_on_results_ms: Date.now() - resultsStartTime.current,
			score,
			category,
		});
		onUpgrade?.();
	}, [tier, score, category, onUpgrade]);

	const renderTopSection = (moduleKey: ResultModuleKey, index: number) => {
		switch (moduleKey) {
			case "summary":
				return (displayState === "idle" || displayState === "complete") && summary ? (
					<View
						key={`${moduleKey}-${index}`}
						style={sharedStyles.glassCard}
						role="region"
						accessibilityLabel={t("home.modern.analysis_summary", {
							defaultValue: "Analysis Summary",
						})}
						testID={`${testID}-summary-card`}
					>
						<TierBody
							size="base"
							color={voiceStyleColors["professional"]}
							style={[narratorStyles.text, narratorStyles.professionalText]}
						>
							{summary}
						</TierBody>
					</View>
				) : null;
			case "primary_rewrite":
				return primaryRewrite ? (
					<View key={`${moduleKey}-${index}`}>
						<PrimaryRewriteCard
							rewriteText={primaryRewrite.rewritten}
							reasoning={primaryRewrite.reasoning}
							showCopyAction={shouldPromotePrimaryActions}
							onCopyAction={trackPrimaryRewriteCopied}
							t={t}
							testID={`${testID}-primary-rewrite-card`}
						/>
					</View>
				) : null;
			case "narrator":
				return (displayState === "idle" || displayState === "complete") &&
					narratorIntro ? (
					<View
						key={`${moduleKey}-${index}`}
						style={[sharedStyles.glassCard, narratorStyles.narratorCard]}
						role="region"
						accessibilityLabel={t("home.modern.narrator_intro", {
							defaultValue: "Analysis Introduction",
						})}
						testID={`${testID}-narrator-card`}
					>
						<TierBody
							size="base"
							color={voiceStyleColors[voiceStyle]}
							style={[narratorStyles.text, narratorStyles.playfulText]}
						>
							{narratorIntro}
						</TierBody>
					</View>
				) : null;
			default:
				return null;
		}
	};

	const shareButton =
		resolvedShareTreatment !== "hidden" ? (
			<View
				style={
					resolvedShareTreatment === "primary"
						? previewStyles.primaryShareButtonContainer
						: previewStyles.secondaryShareButtonContainer
				}
				testID={`${testID}-share-container`}
			>
				<ViralShareButton
					analysis={analysis}
					analysisId={analysis.id}
					variant={
						resolvedShareTreatment === "primary" ? "primary" : "secondary"
					}
					size={resolvedShareTreatment === "primary" ? "lg" : "sm"}
					fullWidth={resolvedShareTreatment === "primary"}
					testID={`${testID}-share-button`}
				/>
			</View>
		) : null;

	const primaryActions = (
		<View style={sharedStyles.actionButtons} testID={`${testID}-primary-actions`}>
			<GlassButton
				onPress={handleAnalyzeAnother}
				variant="primary"
				style={sharedStyles.actionButton}
				testID={`${testID}-new-analysis-button`}
			>
				{t("home.modern.analyze_another", {
					defaultValue: "Analyze Another",
				})}
			</GlassButton>
		</View>
	);

	return (
		<View style={sharedStyles.container} testID={testID}>
			{/* Score Card */}
			<ModernScoreCard
				score={score}
				category={category}
				scoreLabel={t("home.modern.drama_score", {
					defaultValue: "Drama Score",
				})}
				impactLabel={
					likelyImpactLabel
						? t("home.modern.likely_impact_value", {
								defaultValue: "Likely impact: {impact}",
								impact: likelyImpactLabel,
							})
						: null
				}
				testID={`${testID}-score-card`}
			/>

			{/* Loading/Streaming Phase - Show narrator quips */}
			{loadingText && (
				<View
					style={sharedStyles.glassCard}
					role="region"
					accessibilityLabel={t("home.modern.analyzing", {
						defaultValue: "Analyzing...",
					})}
					testID={`${testID}-loading-card`}
				>
					<TierBody
						size="base"
						color={voiceStyleColors[voiceStyle]}
						style={[
							narratorStyles.text,
							voiceStyle === "playful" && narratorStyles.playfulText,
						]}
					>
						{loadingText}
						{showTypingAnimation && (
							<Text style={narratorStyles.cursor}>|</Text>
						)}
					</TierBody>
				</View>
			)}

			{topSectionOrder.map(renderTopSection)}

			{/* Inline Annotations - Tier-gated issue highlighting */}
			{hasIssues && message && (
				<View style={sharedStyles.glassCard}>
					<GatedInlineAnnotations
						message={message}
						issues={issues}
						rewrites={rewrites}
						overrideTier={tier}
						screen="results_preview"
						onUpgrade={handleUpgrade}
						testID={`${testID}-inline-annotations`}
					/>
				</View>
			)}

			{shouldPromotePrimaryActions ? primaryActions : shareButton}
			{shouldPromotePrimaryActions ? shareButton : primaryActions}
		</View>
	);
}

/**
 * Voice style to color mapping for tier-aware narrator text
 * - playful: Purple/primary tone (L1 tier - free/anonymous)
 * - balanced: Gray-700 (L2 tier - drama_plus)
 * - professional: Gray-800 (L3 tier - dramax)
 */
const previewStyles = StyleSheet.create({
	primaryShareButtonContainer: {
		maxWidth: 400,
		alignSelf: "center",
		width: "100%",
		marginTop: 16,
	},
	secondaryShareButtonContainer: {
		marginTop: 16,
		alignSelf: "center",
	},
});

const voiceStyleColors: Record<
	"playful" | "balanced" | "professional",
	string
> = {
	playful: "text-primary-600",
	balanced: "text-gray-700",
	professional: "text-gray-800",
};

/**
 * Narrator-specific styles for voice presentation
 */
const narratorStyles = StyleSheet.create({
	text: {
		lineHeight: 24,
	},
	playfulText: {
		fontStyle: "italic",
	},
	professionalText: {
		fontWeight: "500",
	},
	cursor: {
		opacity: 0.6,
	},
	debugText: {
		fontSize: 10,
		color: "#9ca3af", // gray-400
		marginTop: 8,
	},
	// Style for narrator intro card (visually distinct from summary)
	narratorCard: {
		borderLeftWidth: 3,
		borderLeftColor: "#8b5cf6", // purple-500 (playful accent)
	},
});
